import React from 'react';
import { motion } from 'framer-motion';
import { 
  Briefcase, 
  Calendar, 
  LinkedinIcon, 
  Clock,
  ExternalLink,
  MapPin,
  Star,
  Award
} from 'lucide-react';
import Button from '../ui/Button';
import type { Mentor } from '../../lib/types';

interface MentorCardProps {
  mentor: Mentor;
  onRequestSession: () => void;
  className?: string;
}

const MentorCard: React.FC<MentorCardProps> = ({ 
  mentor,
  onRequestSession,
  className = ''
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      transition={{ duration: 0.2 }}
      className={`bg-white rounded-[2rem] shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden ${className}`}
    >
      <div className="p-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center">
            <img
              src={mentor.avatar_url}
              alt={mentor.name}
              className="h-16 w-16 rounded-full object-cover border-4 border-primary-100"
            />
            <div className="ml-4">
              <h3 className="text-xl font-bold text-gray-900">
                {mentor.name}
              </h3>
              <p className="text-gray-600">
                {mentor.title} at {mentor.company}
              </p>
            </div>
          </div>
          {mentor.linkedin_url && (
            <a
              href={mentor.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-blue-600 hover:text-blue-800 bg-blue-50 rounded-full transition-colors"
            >
              <LinkedinIcon className="h-5 w-5" />
            </a>
          )}
        </div>

        {/* Expertise Areas */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Expertise Areas</h4>
          <div className="flex flex-wrap gap-2">
            {mentor.expertise_areas.map((area, index) => (
              <span
                key={index}
                className="px-3 py-1.5 bg-primary-100 text-primary-800 rounded-lg text-sm font-medium"
              >
                {area}
              </span>
            ))}
          </div>
        </div>

        {/* Details */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center text-gray-600">
            <MapPin className="h-4 w-4 mr-2 text-gray-400" />
            {mentor.location}
          </div>
          <div className="flex items-center text-gray-600">
            <Star className="h-4 w-4 mr-2 text-gray-400" />
            {mentor.years_of_experience} years of experience
          </div>
          <div className="flex items-center text-gray-600">
            <Clock className="h-4 w-4 mr-2 text-gray-400" />
            Available for 15-minute sessions
          </div>
        </div>

        {/* Action Button */}
        <Button
          fullWidth
          onClick={onRequestSession}
          icon={<Calendar className="h-4 w-4" />}
          className="rounded-full"
        >
          Request Session
        </Button>
      </div>
    </motion.div>
  );
};

export default MentorCard;